<template>
  <div>
    <CampaignListSearchAndFilter
      @pagination="pagination"
      @onTextChange="onTextChange"
      :searchAndFilter="searchAndFilter"
      :onActiveFilterChange="onActiveFilterChange"
      :onMenuTypeChange="onMenuTypeChange"
      :campaignList="campaignList"
      :pagination.sync="sortOptions"
    />
    <v-data-table
      :loading="loading"
      :headers="headers"
      :items="campaignList.items"
      :disable-initial-sort="true"
      :rows-per-page-items="[-1]"
      hide-actions
    >
      <template #headers="props">
        <th
          v-for="header in props.headers"
          :key="header.index"
          :class="[
            'column sortable px-2 py-0',
            sortOptions.descending ? 'desc' : 'asc',
            header.value === sortOptions.sortBy ? 'active' : '',
            header.align === 'center' ? 'text-xs-center' : '',
            header.align === 'right' ? 'text-xs-right' : '',
            header.align === 'left' ? 'text-xs-left' : '',
          ]"
          :aria-sort="header.sortable ? '' : 'none'"
          @click="changeSort(header)"
        >
          {{ header.text }}
          <v-icon v-if="header.sortable" small>arrow_upward</v-icon>
        </th>
      </template>

      <template v-slot:items="props">
        <td class="text-xs-center">
          <MdSwitch
            :value="props.item.status !== 'active'"
            :class="[canEditItem(props.item) ? 'md-primary' : '']"
            :disabled="!canEditItem(props.item)"
            @change="changeStatus(props.item)"
          />
        </td>
        <td class="pa-0">{{ props.item.anLineItemId }}</td>
        <td class="text-xs-left">
          <v-tooltip v-if="props.item.isIncompleteTransaction" top>
            <template v-slot:activator="{ on }">
              <v-icon
                v-on="on"
                :class="[
                  'warningIcon',
                  { isVisible: props.item.isIncompleteTransaction },
                  'yellow--text text--darken-1',
                ]"
                small
                >warning</v-icon
              >
            </template>
            <span v-if="props.item.isIncompleteTransaction">
              正常に登録できませんでした。サポートにお問い合わせください
            </span>
          </v-tooltip>
          <v-tooltip v-if="!props.item.isCreativeSet && creativeVisible" top>
            <template v-slot:activator="{ on }">
              <v-icon
                v-on="on"
                :class="[
                  'errorIcon',
                  { isVisible: !props.item.isCreativeSet },
                  'red--text text--darken-1',
                ]"
                small
                >remove_circle</v-icon
              >
            </template>
            <span v-if="!props.item.isCreativeSet">
              クリエイティブが設定されていないため、配信されません。
            </span>
          </v-tooltip>
          {{ props.item.name }}
        </td>
        <td class="pa-0">
          <v-layout>
            <AppMenuIcon :menu-type="props.item.menuType" />
            <MenuType :value="props.item.menuType" class="align-self-center" />
          </v-layout>
        </td>
        <td
          v-if="
            isHideRevenue === false || insertionOrder.permission !== 'readonly'
          "
          class="text-xs-center pa-0"
        >
          <AppUnitType :value="props.item.unitType" />
        </td>
        <td
          v-if="
            isHideRevenue === false || insertionOrder.permission !== 'readonly'
          "
          class="text-xs-center pa-0"
        >
          <AppCurrency :value="props.item.unitPrice" actual-number />
        </td>
        <td class="text-xs-center">
          <AppCreativeType :value="props.item.creativeType" />
        </td>
        <td v-if="creativeVisible" class="text-xs-center">
          <span> {{ props.item.creativeCount }}</span>
        </td>
        <td class="pa-0 text-no-wrap">
          <v-tooltip v-if="canEditItem(props.item)" bottom>
            <template slot="activator">
              <v-btn
                @click="
                  $emit('edit', {
                    insertionOrder: insertionOrder,
                    item: props.item,
                  })
                "
                small
                icon
              >
                <v-icon>edit</v-icon>
              </v-btn>
            </template>
            <span>キャンペーンの編集</span>
          </v-tooltip>

          <v-tooltip v-if="canEditItem(props.item)" bottom>
            <template slot="activator">
              <v-btn
                @click="
                  $emit('duplicate', {
                    insertionOrder: insertionOrder,
                    item: props.item,
                  })
                "
                small
                icon
              >
                <v-icon>file_copy</v-icon>
              </v-btn>
            </template>
            <span>キャンペーンのコピー</span>
          </v-tooltip>

          <v-tooltip v-if="canEditItem(props.item)" bottom>
            <template slot="activator">
              <v-btn @click="deleteItem(props.item)" small icon>
                <v-icon>delete</v-icon>
              </v-btn>
            </template>
            <span>キャンペーンの削除</span>
          </v-tooltip>
        </td>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import MenuType from '../../../Shared/Presentation/Menu/MenuType.vue'
import { CampaignRepository } from '@/models/CampaignOperation'
import IOApi from '@/api/IOApi'
import {
  actions as snackbarActions,
  snackbarType,
} from '@/vuex/modules/ui/snackbar/constants'
import { mapGetters } from 'vuex'
import CampaignListSearchAndFilter from './CampaignListSearchAndFilter'
import { Account } from '@/models/Account'

export default {
  name: 'CampaignTable',
  components: {
    MenuType,
    CampaignListSearchAndFilter,
  },
  props: {
    insertionOrder: {
      type: Object,
      required: true,
    },
    systemOwner: {
      type: Object,
      required: true,
    },
    canEdit: Boolean,
    lastModifiedTimestamp: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      campaignList: {},
      loading: false,
      headers: [],
      sortOptions: {
        sortBy: 'id',
        descending: true,
      },
      searchAndFilter: {
        keyword: '',
        menuType: null,
        activeFilter: { active: true, inactive: true },
        pageNo: 1,
      },
      pageSize: 20,
      menuList: [],
      isHideRevenue: undefined,
    }
  },
  computed: {
    ...mapGetters('account', ['creativeVisible']),
    isSystemOwner() {
      return this.$store.getters['account/isSystemOwner']
    },
    isSuperAdmin() {
      return this.$store.getters['account/isSuperAdmin']
    },
    userBelongType() {
      return this.$store.getters['account/belongType']
    },
    userBelongId() {
      return this.$store.getters['account/belongId']
    },
    userType() {
      return this.$store.getters['account/userType']
    },
  },
  watch: {
    lastModifiedTimestamp() {
      this.initFilterCondition()
      this.setCampaignList()
    },
    async userBelongType() {
      this.setIsHideRevenue()
    },
    isHideRevenue() {
      if (this.isHideRevenue === undefined) return

      const revenueHeaderItems =
        this.isHideRevenue && this.insertionOrder.permission === 'readonly'
          ? []
          : [
              {
                text: '課金形態',
                value: 'unitType',
                class: 'pa-0',
                sortable: false,
              },
              {
                text: '単価',
                value: 'unitPrice',
                class: 'pa-0',
                sortable: false,
              },
            ]

      if (this.creativeVisible) {
        this.headers = [
          { text: 'ステータス', value: 'status', sortable: false },
          { text: 'id', value: 'id', sortable: true, class: 'pa-0' },
          { text: 'キャンペーン名', value: 'name', sortable: true },
          {
            text: 'メニュー種別',
            value: 'menuType',
            sortable: false,
            class: 'pa-0',
          },
          ...revenueHeaderItems,
          {
            text: 'クリエイティブ種別',
            value: 'creativeType',
            sortable: false,
          },
          { text: 'クリエイティブ数', value: 'creativeCount', sortable: false },
          { text: '操作', value: 'action', sortable: false, class: 'pa-0' },
        ]
      } else {
        this.headers = [
          { text: 'ステータス', value: 'status', sortable: false },
          { text: 'id', value: 'id', sortable: true, class: 'pa-0' },
          { text: 'キャンペーン名', value: 'name', sortable: true },
          { text: 'メニュー種別', value: 'menuType', sortable: false },
          {
            text: 'クリエイティブ種別',
            value: 'creativeType',
            sortable: false,
          },
          ...revenueHeaderItems,
          { text: '操作', value: 'action', sortable: false },
        ]
      }
    },
  },
  async mounted() {
    this.$emit('refresh')
  },
  async created() {
    await this.setCampaignList()
    const menus = await IOApi.fetchMenuList(this.insertionOrder.id, {
      isIncludeDeleted: false,
      page_size: 200,
    })
    this.menuList = menus.items
    this.setIsHideRevenue()
  },
  methods: {
    canEditItem(campaign) {
      // (ioへの編集権限がある && campaignがANで作られたものではない) を満たさない場合はfalse
      if (!(this.canEdit && !campaign.isCreatedOnAN)) return false

      // キャンペーンに紐ついているメニューが、アカウントがアクセス可能なメニューであることをチェックし、
      // 条件を満たさない場合はfalse
      if (
        this.menuList.every(menu => menu.id !== campaign.ownerProfileMenuId)
      ) {
        return false
      }
      return true
    },
    async setIsHideRevenue() {
      if (!this.userBelongType || !this.userBelongId) return

      this.isHideRevenue = await Account.isHideRevenue(
        this.userBelongType,
        this.userBelongId,
        this.userType,
      )
    },
    async deleteItem(campaign) {
      // キャンペーン名を削除しますがよろしいですか？
      const confirmed = await this.$confirm(
        `${campaign.name}を削除してもよろしいですか？`,
      )
      if (!confirmed) return

      try {
        await CampaignRepository.delete(campaign.id)
        this.$store.dispatch(snackbarActions.SHOW_SNACKBAR, {
          type: snackbarType.SUCCESS,
          message: 'キャンペーンを削除しました',
        })
        // 再読み込み
        this.$emit('refresh')
      } catch (e) {
        this.$store.dispatch(snackbarActions.SHOW_SNACKBAR, {
          type: snackbarType.ERROR,
          message: e.message,
        })
      }
    },
    async changeStatus(campaign) {
      const nextStatus = campaign.status === 'active' ? 'inactive' : 'active'
      const statusText = nextStatus === 'active' ? '有効' : '無効'
      const confirmed = await this.$confirm(
        `${campaign.name}を${statusText}にしますがよろしいですか？`,
      )
      if (confirmed) {
        try {
          await CampaignRepository.changeStatus(campaign.id, nextStatus)
          this.$store.dispatch(snackbarActions.SHOW_SNACKBAR, {
            type: snackbarType.SUCCESS,
            message: 'ステータスを変更しました',
          })
          // 再読み込み
          this.$emit('refresh')
        } catch (e) {
          console.error(e)
          this.$store.dispatch(snackbarActions.SHOW_SNACKBAR, {
            type: snackbarType.ERROR,
            message: 'ステータスの変更に失敗しました',
          })
        }
      }
    },
    iconWarning(campaign) {
      return campaign.isIncompleteTransaction ? 'warn' : ''
    },
    initFilterCondition() {
      this.searchAndFilter = {
        pageNo: 1,
        keyword: '',
        menuType: null,
        activeFilter: { active: true, inactive: true },
      }
      this.sortOptions = {
        sortBy: 'id',
        descending: true,
      }
    },
    async pagination(pageNo) {
      this.searchAndFilter.pageNo = pageNo
      await this.setCampaignList()
    },
    async onTextChange(keyword) {
      this.searchAndFilter.keyword = keyword
      this.searchAndFilter.pageNo = 1
      await this.setCampaignList()
    },
    async onActiveFilterChange(e) {
      const state = !this.searchAndFilter.activeFilter[e]
      this.searchAndFilter.activeFilter[e] = state
      this.searchAndFilter.pageNo = 1
      await this.setCampaignList()
    },
    async onMenuTypeChange(menuType) {
      this.searchAndFilter.menuType = menuType
      this.searchAndFilter.pageNo = 1
      await this.setCampaignList()
    },
    async setCampaignList() {
      this.loading = true
      const res = await CampaignRepository.search({
        advertiserId: this.insertionOrder.advertiserId,
        insertionOrder: {
          ids: [this.insertionOrder.id],
        },
        keyword: this.searchAndFilter.keyword,
        menuType: this.searchAndFilter.menuType,
        campaignStatuses: this.getFilteredStatuses(),
        pageNo: this.searchAndFilter.pageNo,
        pageSize: this.pageSize,
        sortKey: this.sortOptions.sortBy,
        sortDirection: this.sortOptions.descending ? 'desc' : 'asc',
      })
      res['pageSize'] = this.pageSize
      this.campaignList = res
      this.loading = false
    },
    getFilteredStatuses() {
      const active = this.searchAndFilter.activeFilter.active
      const inactive = this.searchAndFilter.activeFilter.inactive
      if (active && !inactive) {
        return ['active']
      } else if (!active && inactive) {
        return ['inactive']
      } else if (!active && !inactive) {
        return []
      } else {
        return ['active', 'inactive']
      }
    },
    changeSort(header) {
      if (!header.sortable) return
      const column = header.value
      const newSortOptions = {
        ...this.sortOptions,
      }
      if (newSortOptions.sortBy === column) {
        newSortOptions.descending = !newSortOptions.descending
      } else {
        newSortOptions.sortBy = column
        newSortOptions.descending = false
      }
      this.sortOptions.sortBy = newSortOptions.sortBy
      this.sortOptions.descending = newSortOptions.descending
      this.setCampaignList()
    },
  },
}
</script>

<style lang="scss" scoped>
.warningIcon {
  visibility: hidden;

  &.isVisible {
    visibility: visible;
  }
}
</style>
